import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addArticleToVolumeTypes,
  CreateVolumePayload,
  publishVolumeTypes,
  removeArticleFromVolumeTypes,
  requestVolumeTypes,
  setArticleOrderOnVolumeTypes,
  SuccesType,
  VolumeProduction,
  volumesTypes,
} from "./volumesTypes";
import volumesService from "./volumesService";

interface volumesState {
  volumes: volumesTypes[] | null;
  volume: volumesTypes | null;
  volumesProduction: VolumeProduction[] | null;

  loadingVolumes: boolean;
  loadingVolume: boolean;
  loadingPDF: boolean;
  loadingArticleOps: boolean;
  loadingDownload: boolean;
  error: any | null;
}

const initialState: volumesState = {
  volumes: null,
  volume: null,
  volumesProduction: null,

  loadingVolumes: false,
  loadingVolume: false,
  loadingPDF: false,
  loadingArticleOps: false,
  loadingDownload: false,
  error: null,
};

export const getVolumes = createAsyncThunk<
  volumesTypes[],
  void,
  { rejectValue: string }
>("volumes/getVolumes", async (_, thunkAPI) => {
  try {
    const res = await volumesService.getVolumes();
    return res;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const getAllVolumes = createAsyncThunk<
  volumesTypes[],
  void,
  { rejectValue: string }
>("volumes/getAllVolumes", async (_, thunkAPI) => {
  try {
    const res = await volumesService.getAllVolumes();
    return res;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const getVolume = createAsyncThunk<
  volumesTypes,
  string,
  { rejectValue: string }
>("volumes/getVolume", async (slug, thunkAPI) => {
  try {
    const res = await volumesService.getVolume(slug);
    return res;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const getVolumesProduction = createAsyncThunk<
  VolumeProduction[],
  void,
  { rejectValue: string }
>("volumes/getVolumesProduction", async (_, thunkAPI) => {
  try {
    const res = await volumesService.getVolumesProduction();
    return res;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error);
  }
});

export const createVolume = createAsyncThunk<
  volumesTypes,
  CreateVolumePayload,
  { rejectValue: string }
>("volumes/createVolume", async (data, { rejectWithValue }) => {
  try {
    const response = await volumesService.createVolume(data);
    return response as volumesTypes;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const editVolume = createAsyncThunk<
  volumesTypes,
  requestVolumeTypes,
  { rejectValue: string }
>("volumes/editVolume", async (data, { rejectWithValue }) => {
  try {
    const response = await volumesService.editVolume(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const registerVolumeDownload = createAsyncThunk<
  SuccesType,
  string,
  { rejectValue: string }
>(
  "volumes/registerVolumeDownload",
  async (volume_slug: string, { rejectWithValue }) => {
    try {
      const res = await volumesService.registerVolumeDownload(volume_slug);
      return res as SuccesType;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const registerVolumeDownloadNoLogin = createAsyncThunk<
  SuccesType,
  string,
  { rejectValue: string }
>(
  "volumes/registerVolumeDownloadNoLogin",
  async (volume_slug: string, { rejectWithValue }) => {
    try {
      const res =
        await volumesService.registerVolumeDownloadNoLogin(volume_slug);
      return res as SuccesType;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Ocurrió un error inesperado al registrar la descarga.";
      return rejectWithValue(errorMessage);
    }
  },
);

export const getVolumeBySlug = createAsyncThunk<
  volumesTypes,
  string,
  { rejectValue: string }
>("volumes/getVolumeBySlug", async (volume_slug, { rejectWithValue }) => {
  try {
    const res = await volumesService.getVolumeBySlug(volume_slug);
    return res;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const deleteVolume = createAsyncThunk<
  SuccesType,
  string,
  { rejectValue: string }
>("volumes/deleteVolume", async (slug: string, { rejectWithValue }) => {
  try {
    const res = await volumesService.deleteVolume(slug);
    return res as SuccesType;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const deleteFileFromVolume = createAsyncThunk<
  SuccesType,
  string,
  { rejectValue: string }
>("volumes/deleteFileFromVolume", async (slug: string, { rejectWithValue }) => {
  try {
    const res = await volumesService.deleteFileFromVolume(slug);
    return res as SuccesType;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const addArticleToVolume = createAsyncThunk<
  SuccesType,
  addArticleToVolumeTypes,
  { rejectValue: string }
>(
  "volumes/addArticleToVolume",
  async (data: addArticleToVolumeTypes, { rejectWithValue }) => {
    try {
      const response = await volumesService.addArticleToVolume(data);
      return response as SuccesType;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const removeArticleFromVolume = createAsyncThunk<
  SuccesType,
  removeArticleFromVolumeTypes,
  { rejectValue: string }
>(
  "volumes/removeArticleToVolume",
  async (data: removeArticleFromVolumeTypes, { rejectWithValue }) => {
    try {
      const response = await volumesService.removeArticleFromVolume(data);
      return response as SuccesType;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const addPDFVolume = createAsyncThunk<
  SuccesType,
  FormData,
  { rejectValue: string }
>("volumes/addPDFVolume", async (formData: FormData, { rejectWithValue }) => {
  try {
    const response = await volumesService.addPDFVolume(formData);
    return response as SuccesType;
  } catch (error: any) {
    if (error) {
      return rejectWithValue(error);
    }
    return rejectWithValue(error);
  }
});

export const publishVolume = createAsyncThunk<
  SuccesType,
  publishVolumeTypes,
  { rejectValue: string }
>(
  "volumes/publishVolume",
  async (data: publishVolumeTypes, { rejectWithValue }) => {
    try {
      const response = await volumesService.publishVolume(data);
      return response as SuccesType;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const setArticleOrderOnVolume = createAsyncThunk<
  setArticleOrderOnVolumeTypes,
  setArticleOrderOnVolumeTypes,
  { rejectValue: string }
>(
  "volumes/setArticleOrderOnVolume",
  async (
    setArticleOrderOnVolumeRequest: setArticleOrderOnVolumeTypes,
    { rejectWithValue },
  ) => {
    try {
      await volumesService.setArticleOrderOnVolume(
        setArticleOrderOnVolumeRequest,
      );
      return setArticleOrderOnVolumeRequest;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  },
);
export const getInitialVolume = createAsyncThunk<
  volumesTypes,
  void,
  { rejectValue: string }
>("volumes/getInitialVolume", async (_, thunkAPI) => {
  try {
    const res = await volumesService.getInitialVolume();
    return res;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error);
  }
});
const volumesSlice = createSlice({
  name: "volumes",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // getVolumes
    builder
      .addCase(getVolumes.pending, (state) => {
        state.loadingVolumes = true;
        state.error = null;
      })
      .addCase(getVolumes.fulfilled, (state, action) => {
        state.loadingVolumes = false;
        state.volumes = action.payload;
      })
      .addCase(getVolumes.rejected, (state, action) => {
        state.loadingVolumes = false;
        state.error = action.payload || "Error al cargar volúmenes";
      });

    // getAllVolumes
    builder
      .addCase(getAllVolumes.pending, (state) => {
        state.loadingVolumes = true;
        state.error = null;
      })
      .addCase(getAllVolumes.fulfilled, (state, action) => {
        state.loadingVolumes = false;
        state.volumes = action.payload;
      })
      .addCase(getAllVolumes.rejected, (state, action) => {
        state.loadingVolumes = false;
        state.error = action.payload || "Error al cargar todos los volúmenes";
      });

    // createVolume
    builder
      .addCase(createVolume.pending, (state) => {
        state.loadingVolumes = true;
        state.error = null;
      })
      .addCase(createVolume.fulfilled, (state, action) => {
        state.loadingVolumes = false;
        state.volumes = state.volumes
          ? [...state.volumes, action.payload]
          : [action.payload];
      })
      .addCase(createVolume.rejected, (state, action) => {
        state.loadingVolumes = false;
        state.error = (action.payload as string) || "Error al crear volumen";
      });

    // deleteVolume
    builder
      .addCase(deleteVolume.pending, (state) => {
        state.loadingVolumes = true;
        state.error = null;
      })
      .addCase(deleteVolume.fulfilled, (state, action) => {
        state.loadingVolumes = false;
        if (state.volumes) {
          state.volumes = state.volumes.filter(
            (v) => v.slug !== action.meta.arg,
          );
        }
      })
      .addCase(deleteVolume.rejected, (state, action) => {
        state.loadingVolumes = false;
        state.error = (action.payload as string) || "Error al eliminar volumen";
      });

    // getVolume
    builder
      .addCase(getVolume.pending, (state) => {
        state.loadingVolume = true;
        state.error = null;
      })
      .addCase(getVolume.fulfilled, (state, action) => {
        state.loadingVolume = false;
        state.volume = action.payload;
      })
      .addCase(getVolume.rejected, (state, action) => {
        state.loadingVolume = false;
        state.error = action.payload || "Error al cargar volumen";
      });

    // editVolume
    builder
      .addCase(editVolume.pending, (state) => {
        state.loadingVolume = true;
        state.error = null;
      })
      .addCase(editVolume.fulfilled, (state, action) => {
        state.loadingVolume = false;
        state.volume = action.payload;
      })
      .addCase(editVolume.rejected, (state, action) => {
        state.loadingVolume = false;
        state.error = action.payload || "Error al editar volumen";
      });

    // getVolumeBySlug
    builder
      .addCase(getVolumeBySlug.pending, (state) => {
        state.loadingVolume = true;
        state.error = null;
      })
      .addCase(getVolumeBySlug.fulfilled, (state, action) => {
        state.loadingVolume = false;
        state.volume = action.payload;
      })
      .addCase(getVolumeBySlug.rejected, (state, action) => {
        state.loadingVolume = false;
        state.error = action.payload || "Error al cargar volumen por slug";
      });

    // publishVolume
    builder
      .addCase(publishVolume.pending, (state) => {
        state.loadingVolume = true;
        state.error = null;
      })
      .addCase(publishVolume.fulfilled, (state) => {
        state.loadingVolume = false;
      })
      .addCase(publishVolume.rejected, (state, action) => {
        state.loadingVolume = false;
        state.error = (action.payload as string) || "Error al publicar volumen";
      });
    // getVolumesProduction
    builder
      .addCase(getVolumesProduction.pending, (state) => {
        state.loadingVolumes = true;
        state.error = null;
      })
      .addCase(getVolumesProduction.fulfilled, (state, action) => {
        state.loadingVolumes = false;
        state.volumesProduction = action.payload;
      })
      .addCase(getVolumesProduction.rejected, (state, action) => {
        state.loadingVolumes = false;
        state.error = action.payload || "Error al cargar volúmenes";
      });

    // deleteFileFromVolume
    builder
      .addCase(deleteFileFromVolume.pending, (state) => {
        state.loadingPDF = true;
        state.error = null;
      })
      .addCase(deleteFileFromVolume.fulfilled, (state) => {
        state.loadingPDF = false;
      })
      .addCase(deleteFileFromVolume.rejected, (state, action) => {
        state.loadingPDF = false;
        state.error = (action.payload as string) || "Error al eliminar archivo";
      });

    // addPDFVolume
    builder
      .addCase(addPDFVolume.pending, (state) => {
        state.loadingPDF = true;
        state.error = null;
      })
      .addCase(addPDFVolume.fulfilled, (state) => {
        state.loadingPDF = false;
      })
      .addCase(addPDFVolume.rejected, (state, action) => {
        state.loadingPDF = false;
        state.error = (action.payload as string) || "Error al subir PDF";
      });

    // addArticleToVolume
    builder
      .addCase(addArticleToVolume.pending, (state) => {
        state.loadingArticleOps = true;
        state.error = null;
      })
      .addCase(addArticleToVolume.fulfilled, (state) => {
        state.loadingArticleOps = false;
      })
      .addCase(addArticleToVolume.rejected, (state, action) => {
        state.loadingArticleOps = false;
        state.error = (action.payload as string) || "Error al agregar artículo";
      });

    // removeArticleFromVolume
    builder
      .addCase(removeArticleFromVolume.pending, (state) => {
        state.loadingArticleOps = true;
        state.error = null;
      })
      .addCase(removeArticleFromVolume.fulfilled, (state) => {
        state.loadingArticleOps = false;
      })
      .addCase(removeArticleFromVolume.rejected, (state, action) => {
        state.loadingArticleOps = false;
        state.error = (action.payload as string) || "Error al remover artículo";
      });

    // setArticleOrderOnVolume
    builder
      .addCase(setArticleOrderOnVolume.pending, (state) => {
        state.loadingArticleOps = true;
        state.error = null;
      })
      .addCase(setArticleOrderOnVolume.fulfilled, (state) => {
        state.loadingArticleOps = false;
      })
      .addCase(setArticleOrderOnVolume.rejected, (state, action) => {
        state.loadingArticleOps = false;
        state.error = action.payload || "Error al ordenar artículos";
      });

    // registerVolumeDownload
    builder
      .addCase(registerVolumeDownload.pending, (state) => {
        state.loadingDownload = true;
        state.error = null;
      })
      .addCase(registerVolumeDownload.fulfilled, (state) => {
        state.loadingDownload = false;
      })
      .addCase(registerVolumeDownload.rejected, (state, action) => {
        state.loadingDownload = false;
        state.error =
          (action.payload as string) || "Error al registrar descarga";
      });

    // registerVolumeDownloadNoLogin
    builder
      .addCase(registerVolumeDownloadNoLogin.pending, (state) => {
        state.loadingDownload = true;
        state.error = null;
      })
      .addCase(registerVolumeDownloadNoLogin.fulfilled, (state) => {
        state.loadingDownload = false;
      })
      .addCase(registerVolumeDownloadNoLogin.rejected, (state, action) => {
        state.loadingDownload = false;
        state.error =
          (action.payload as string) || "Error al registrar descarga";
      });

    // getInitialVolume (si lo usas)
    builder
      .addCase(getInitialVolume.pending, (state) => {
        state.loadingVolume = true;
        state.error = null;
      })
      .addCase(getInitialVolume.fulfilled, (state, action) => {
        state.loadingVolume = false;
        state.volume = action.payload;
      })
      .addCase(getInitialVolume.rejected, (state, action) => {
        state.loadingVolume = false;
        state.error = action.payload || "Error al cargar volumen inicial";
      });
  },
});
export default volumesSlice.reducer;
